import { Spacer, Text } from "@hackthenorth/north";
import moment from "moment-timezone";
import { Button, Select } from "north.js";
import React, { useCallback, useState } from "react";
import { Prompt } from "react-router-dom";

import { successToast } from "src/shared/components";
import { CURRENT_EVENT_SLUG } from "src/shared/constants/event";

import BusCard from "./busCard";
import {
  createEmptyBusSelection,
  createRegisteredBusSelection,
  hasBusSelectionChanged,
  resetBusSelection,
  selectBus,
} from "./busSelection";
import { BUS_DROPOFF_DATE, BUS_LOCATIONS, BUS_PICKUP_DATE } from "./constants";
import { useFindBusesByLocationQuery } from "./graphql/findBusesByLocation.generated";
import { useFindMyBusesByEventQuery } from "./graphql/findMyBusesByEvent.generated";
import { useRegisterSelfToBusMutation } from "./graphql/registerSelfToBus.generated";
import { useUnregisterSelfFromBusMutation } from "./graphql/unregisterSelfToBus.generated";

const UNIVERSITY_OF_WATERLOO = "University of Waterloo";
const LAZARIDIS_HALL = "Lazaridis Hall";

interface BusComponentProps {
  isBlocking: boolean;
  setIsBlocking: React.Dispatch<React.SetStateAction<boolean>>;
  description?: React.ReactNode;
  isReadOnly: boolean;
}

export const BusComponent: React.FC<BusComponentProps> = ({
  isBlocking,
  setIsBlocking,
  description,
  isReadOnly,
}) => {
  const [pickupLocation, setPickupLocation] = useState<string>("");
  const [tempPickupLocation, setTempPickupLocation] = useState<string>("");

  const [dropoffLocation, setDropoffLocation] = useState<string>("");
  const [tempDropoffLocation, setTempDropoffLocation] = useState<string>("");

  const [selectedDropoff, setSelectedDropoff] = useState(
    createEmptyBusSelection
  );

  const [selectedPickup, setSelectedPickup] = useState(createEmptyBusSelection);

  const [changingBus, setChangingBus] = useState({
    pickup: false,
    dropoff: false,
  });

  useFindMyBusesByEventQuery({
    variables: {
      eventSlug: CURRENT_EVENT_SLUG,
    },
    onCompleted: (data) => {
      const pickupBus = data.findMyBusesByEvent.find((bus) =>
        bus.stops?.find((stop) => {
          return (
            moment(stop.departure_time)
              .tz("America/Toronto")
              .format("MMMM D, YYYY") === BUS_PICKUP_DATE
          );
        })
      );

      const dropoffBus = data.findMyBusesByEvent.find((bus) =>
        bus.stops?.find((stop) => {
          return (
            moment
              .utc(stop.departure_time)
              .utcOffset(-5)
              .format("MMMM D, YYYY") === BUS_DROPOFF_DATE
          );
        })
      );

      const pickupStop = pickupBus?.stops?.[0];
      const dropoffStop = dropoffBus?.stops?.[0];

      if (pickupBus && pickupStop) {
        setSelectedPickup(
          createRegisteredBusSelection(pickupBus.id, pickupStop.id)
        );
        setPickupLocation(pickupStop.name);
      }

      if (dropoffBus && dropoffStop) {
        setSelectedDropoff(
          createRegisteredBusSelection(dropoffBus.id, dropoffStop.id)
        );
        setDropoffLocation(dropoffStop.name);
      }
    },
  });

  const { data: departureBuses, refetch: refetchDepartureBuses } =
    useFindBusesByLocationQuery({
      variables: {
        location: pickupLocation,
      },
      skip: !pickupLocation,
    });

  const validDepartureBuses = departureBuses?.findBusesByLocation
    .map((bus) => {
      const departureStop = bus.stops?.find(
        (stop) => stop.name === pickupLocation
      );
      const arrivalStop = bus.stops?.[bus.stops.length - 1];

      return !departureStop ||
        !arrivalStop ||
        (arrivalStop.name !== UNIVERSITY_OF_WATERLOO &&
          arrivalStop.name !== LAZARIDIS_HALL)
        ? null
        : { ...bus, departureStop, arrivalStop };
    })
    .filter((bus): bus is NonNullable<typeof bus> => bus !== null)
    .sort((a, b) =>
      a.departureStop.departure_time.localeCompare(
        b.departureStop.departure_time
      )
    );

  const { data: arrivalBuses, refetch: refetchArrivalBuses } =
    useFindBusesByLocationQuery({
      variables: {
        location: dropoffLocation,
      },
      skip: !dropoffLocation,
    });

  const validArrivalBuses = arrivalBuses?.findBusesByLocation
    .map((bus) => {
      const departureStop = bus.stops?.[0];
      const arrivalStop = bus.stops?.find(
        (stop) => stop.name === dropoffLocation
      );

      return !departureStop ||
        !arrivalStop ||
        (departureStop.name !== UNIVERSITY_OF_WATERLOO &&
          departureStop.name !== LAZARIDIS_HALL)
        ? null
        : { ...bus, departureStop, arrivalStop };
    })
    .filter((bus): bus is NonNullable<typeof bus> => bus !== null)
    .sort((a, b) =>
      a.departureStop.departure_time.localeCompare(
        b.departureStop.departure_time
      )
    );

  const [registerSelfToBus] = useRegisterSelfToBusMutation();

  const [unregisterSelfToBus] = useUnregisterSelfFromBusMutation();

  const registerToPickupBus = useCallback(async () => {
    setChangingBus({ ...changingBus, pickup: false });
    if (selectedPickup.currentBusId !== -1) {
      await unregisterSelfToBus({
        variables: {
          busId: selectedPickup.currentBusId,
        },
      });
    }

    if (selectedPickup.selectedBusStopId !== -1) {
      const result = await registerSelfToBus({
        variables: {
          busStopId: selectedPickup.selectedBusStopId,
        },
      });

      const newBusID = result.data?.registerSelfToBus?.bus_id;

      await refetchDepartureBuses();
      if (typeof newBusID === "number") {
        setSelectedPickup(
          createRegisteredBusSelection(
            newBusID,
            selectedPickup.selectedBusStopId
          )
        );
      }
      successToast("Successfully registered to pickup bus!");
      setChangingBus({ ...changingBus, pickup: false });
      return;
    }
    await refetchDepartureBuses();
    setSelectedPickup(createEmptyBusSelection());
    successToast("Successfully unregistered from pickup bus!");
    setChangingBus({ ...changingBus, pickup: false });
  }, [
    changingBus,
    registerSelfToBus,
    selectedPickup,
    unregisterSelfToBus,
    refetchDepartureBuses,
  ]);

  const registerToDropoffBus = useCallback(async () => {
    if (selectedDropoff.currentBusId !== -1) {
      await unregisterSelfToBus({
        variables: {
          busId: selectedDropoff.currentBusId,
        },
      });
    }

    if (selectedDropoff.selectedBusStopId !== -1) {
      const result = await registerSelfToBus({
        variables: {
          busStopId: selectedDropoff.selectedBusStopId,
        },
      });

      const newBusID = result.data?.registerSelfToBus?.bus_id;

      await refetchArrivalBuses();
      if (typeof newBusID === "number") {
        setSelectedDropoff(
          createRegisteredBusSelection(
            newBusID,
            selectedDropoff.selectedBusStopId
          )
        );
      }
      successToast("Successfully registered to dropoff bus!");
      setChangingBus({ ...changingBus, dropoff: false });
      return;
    }

    await refetchArrivalBuses();
    setSelectedDropoff(createEmptyBusSelection());
    successToast("Successfully unregistered from dropoff bus!");
    setChangingBus({ ...changingBus, dropoff: false });
  }, [
    changingBus,
    registerSelfToBus,
    selectedDropoff,
    unregisterSelfToBus,
    refetchArrivalBuses,
  ]);

  return (
    <>
      <Prompt
        when={isBlocking}
        message="You have unsaved changes, are you sure you want to move away from this page?"
      />
      <Text mods="h3 bold">Reserve your bus</Text>
      {description && (
        <>
          <Spacer height={12} />
          <Text>{description}</Text>
        </>
      )}
      <Spacer height={24} />
      <Text mods="bold textBody">Pick-up Location (Friday, September 18)</Text>
      <Spacer height={12} />
      <Select
        fullWidth
        size="lg"
        value={pickupLocation}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          setSelectedPickup((selection) => selectBus(selection, -1, -1));
          setPickupLocation(e.target.value);
        }}
        disabled={isReadOnly || !changingBus.pickup}
      >
        {BUS_LOCATIONS.map((route, i) => {
          return (
            <Select.Option key={i} value={route}>
              {route}
            </Select.Option>
          );
        })}
        <Select.Option value={""}>I don&apos;t need a bus</Select.Option>
      </Select>
      <Spacer height={24} />
      {pickupLocation !== "" && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            width: "95%",
            marginBottom: "24px",
          }}
        >
          {validDepartureBuses?.map((bus) => (
            <BusCard
              key={bus.id}
              availableSeats={bus.seats_available}
              departure={bus.departureStop.departure_time}
              arrival={bus.arrivalStop.departure_time}
              selected={bus.id === selectedPickup.selectedBusId}
              disabled={
                isReadOnly || bus.seats_available === 0 || !changingBus.pickup
              }
              onClick={() => {
                setSelectedPickup((selection) =>
                  selectBus(selection, bus.id, bus.departureStop.id)
                );
                setIsBlocking(true);
              }}
            />
          ))}
        </div>
      )}
      {!isReadOnly && (
        <div style={{ display: "flex", gap: "5px" }}>
          <Button
            color={changingBus.pickup ? "primary" : "secondary"}
            size="lg"
            onClick={() => {
              if (changingBus.pickup) {
                registerToPickupBus();
              } else {
                setChangingBus({ ...changingBus, pickup: true });
                setTempPickupLocation(pickupLocation);
              }
            }}
            disabled={
              changingBus.pickup &&
              (!hasBusSelectionChanged(selectedPickup) ||
                (pickupLocation !== "" && selectedPickup.selectedBusId === -1))
            }
          >
            {changingBus.pickup ? "Confirm" : "Edit bus route"}
          </Button>
          {changingBus.pickup && (
            <Button
              color="secondary"
              size="lg"
              onClick={() => {
                setChangingBus({ ...changingBus, pickup: false });
                setSelectedPickup((selection) => resetBusSelection(selection));
                setPickupLocation(tempPickupLocation);
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      )}
      <Spacer height={36} />
      <Text mods="bold textBody">Drop-off Location (Sunday, September 20)</Text>
      <Spacer height={12} />
      <Select
        fullWidth
        size="lg"
        value={dropoffLocation}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          setSelectedDropoff((selection) => selectBus(selection, -1, -1));
          setDropoffLocation(e.target.value);
        }}
        disabled={isReadOnly || !changingBus.dropoff}
      >
        {BUS_LOCATIONS.map((route, i) => (
          <Select.Option key={i} value={route}>
            {route}
          </Select.Option>
        ))}
        <Select.Option value={""}>I don&apos;t need a bus</Select.Option>
      </Select>
      <Spacer height={24} />
      {dropoffLocation !== "" && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            width: "95%",
            marginBottom: "24px",
          }}
        >
          {validArrivalBuses?.map((bus) => (
            <BusCard
              key={bus.id}
              availableSeats={bus.seats_available}
              departure={bus.departureStop.departure_time}
              arrival={bus.arrivalStop.departure_time}
              selected={bus.id === selectedDropoff.selectedBusId}
              disabled={
                isReadOnly || bus.seats_available === 0 || !changingBus.dropoff
              }
              onClick={() => {
                setSelectedDropoff((selection) =>
                  selectBus(selection, bus.id, bus.arrivalStop.id)
                );
                setIsBlocking(true);
              }}
            />
          ))}
        </div>
      )}
      {!isReadOnly && (
        <div style={{ display: "flex", gap: "5px" }}>
          <Button
            color={changingBus.dropoff ? "primary" : "secondary"}
            size="lg"
            onClick={() => {
              if (changingBus.dropoff) {
                registerToDropoffBus();
              } else {
                setChangingBus({ ...changingBus, dropoff: true });
                setTempDropoffLocation(dropoffLocation);
              }
            }}
            disabled={
              changingBus.dropoff &&
              (!hasBusSelectionChanged(selectedDropoff) ||
                (dropoffLocation !== "" &&
                  selectedDropoff.selectedBusId === -1))
            }
          >
            {changingBus.dropoff ? "Confirm" : "Edit bus route"}
          </Button>
          {changingBus.dropoff && (
            <Button
              color="secondary"
              size="lg"
              onClick={() => {
                setChangingBus({ ...changingBus, dropoff: false });
                setSelectedDropoff((selection) => resetBusSelection(selection));
                setDropoffLocation(tempDropoffLocation);
              }}
            >
              Cancel
            </Button>
          )}
        </div>
      )}
    </>
  );
};
